import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { supabase } from '../../../supabase/client';

// Format timestamp to readable date/time
const fmtFullTime = (iso) =>
  new Date(iso).toLocaleString([], {
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

// Overlay for editing/deleting own messages
function MessageMenuOverlay({ message, chatId, onClose }) {
  const currentUserId = useSelector((s) => s.user.authUserId);
  const chats = useSelector((s) => s.chats.chats);

  // Local state for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(message.body);
  const textareaRef = useRef(null);

  // Close overlay and reset edit state
  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  // Toggle edit mode, focus textarea when entering
  const handleEdit = () => {
    if (!isEditing) {
      setIsEditing(true);
      // Focus textarea and move cursor to end after state update
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      }, 10);
    } else {
      setIsEditing(false);
    }
  };

  // Cancel edit and reset text to original
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText(message.body);
  };

  // Save edited message to database
  const handleSaveEdit = async () => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({
          body: editedText,
          edited: editedText,
          edited_at: new Date().toISOString()
        })
        .eq('id', message.id);

      if (error) throw error;

      setIsEditing(false);
      onClose();
    } catch (error) {
      console.error('[MessageMenuOverlay] Save error:', error);
    }
  };

  // Delete or restore message
  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({
          deleted: !message.deleted,
          deleted_at: new Date().toISOString()
        })
        .eq('id', message.id);

      if (error) throw error;

      onClose();
    } catch (error) {
      console.error('[MessageMenuOverlay] Delete/Restore error:', error);
    }
  };

  // Format timestamps for display
  const fullSentTime = fmtFullTime(message.created_at);
  const readBy = message.read_by || {};

  // Find when the other user read this message (for "Seen" timestamp)
  const chat = message.chat_id ? chats[message.chat_id] : null;
  const otherUserId = chat?.user_ids?.find(id => id !== currentUserId);
  const readTimestamp = otherUserId && otherUserId in readBy ? readBy[otherUserId] : null;
  const fullSeenTime = readTimestamp ? fmtFullTime(readTimestamp) : null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-card p-5 shadow-2xl text-black">
        {/* Action buttons at top */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Edit Message</h2>
          <button onClick={handleClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <button
            onClick={handleEdit}
            className="w-full rounded-xl border border-border bg-accent px-4 py-3 text-sm font-medium transition-colors"
          >
            {isEditing ? 'Stop Editing' : 'Edit Message'}
          </button>
          <button
            onClick={handleDelete}
            className="w-full rounded-xl border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive hover:bg-destructive/20 transition-colors"
          >
            {message.deleted ? 'Restore Message' : 'Delete Message'}
          </button>
        </div>

        {/* Message display with actual message styles */}
        <div className="flex flex-col items-center">
          <div
            className="relative max-w-[75%] min-w-[40px] min-h-[40px] rounded-2xl pl-3 pr-6 pt-2 pb-2 text-sm rounded-tr-sm bg-primary text-white"
          >
            {isEditing ? (
              <textarea
                ref={textareaRef}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                className="w-full bg-white/20 text-sm focus:outline-none resize-none min-h-[60px] p-2"
                rows={3}
              />
            ) : (
              <p className="whitespace-pre-wrap">{message.body}</p>
            )}
          </div>
          {/* Timestamp display */}
          <div className="mt-2 text-xs text-muted-foreground space-y-1">
            <div>Sent {fullSentTime}</div>
            {fullSeenTime && (
              <div>Seen {fullSeenTime}</div>
            )}
          </div>
        </div>

        {/* Bottom buttons */}
        <div className="mt-4">
          {isEditing ? (
            <div className="flex gap-2">
              <button
                onClick={handleCancelEdit}
                className="flex-1 rounded-xl border border-border px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
              >
                Clear & Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
              >
                Save
              </button>
            </div>
          ) : (
            <button
              onClick={handleClose}
              className="w-full rounded-xl bg-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
            >
              Cancel & Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageMenuOverlay;
